/**
 * Step 3 — Commercials and payment terms.
 *
 * Line-item labels come from the approved template. Sales enters an amount,
 * decides whether the line is included, and may apply a discount. The total is
 * calculated live and is the net figure that will appear in the document.
 *
 * On discounts: the approved Investment table has no discount row, so a
 * discount reduces the amount that is printed. The gross figure and the
 * percentage are stored for internal reporting, which keeps the document
 * identical in structure to the approved template.
 */
import type {
  CommercialSummary,
  FamilyRules,
  PaymentTerm,
  ProposalConfiguration,
  TemplateManifest,
} from '../../lib/api';
import { Card, Field, Input, Select, formatMoney } from '../../components/ui';

export function CommercialsStep({
  config,
  update,
  disabled,
  flagged,
  manifest,
  paymentTerms,
  rules,
  commercials,
}: {
  config: ProposalConfiguration;
  update<K extends keyof ProposalConfiguration>(key: K, value: ProposalConfiguration[K]): void;
  disabled: boolean;
  flagged: Set<string>;
  manifest: TemplateManifest;
  paymentTerms: PaymentTerm[];
  rules: FamilyRules;
  commercials: CommercialSummary | null;
}) {
  const currency = config.currency ?? 'PHP';
  const amounts = config.amounts ?? {};
  const discounts = config.discounts ?? {};

  const mixed = rules.commercialModel === 'ONE_TIME_AND_RECURRING';
  const recurringSuffix = rules.recurringSuffix ?? '/ month';
  const kindOf = (id: string) =>
    mixed ? (rules.commercialKinds?.[id] ?? 'ONE_TIME') : 'ONE_TIME';

  // A tier selection decides which retainer applies, so the other tiers are not
  // offered as check boxes at all — quoting all three at once would be nonsense.
  const tier = rules.tierDrivesCommercialItems;
  const tierItemIds = new Set(Object.values(tier?.itemByValue ?? {}));
  const selectedTierValue = tier ? config.engagement?.[tier.fieldKey] : undefined;
  const tierItemId =
    tier && typeof selectedTierValue === 'string'
      ? tier.itemByValue[selectedTierValue]
      : undefined;

  const visibleItems = manifest.commercialItems.filter(
    (item) => !tierItemIds.has(item.id) || item.id === tierItemId,
  );

  const includedIds = (
    config.commercialItemIds ??
    manifest.commercialItems.filter((i) => i.includedByDefault !== false).map((i) => i.id)
  ).filter((id) => !tierItemIds.has(id) || id === tierItemId);

  const setIncluded = (id: string, included: boolean) => {
    const next = included
      ? [...new Set([...includedIds, id])]
      : includedIds.filter((existing) => existing !== id);
    // Preserve the approved ordering, whatever order the boxes were ticked in.
    update(
      'commercialItemIds',
      manifest.commercialItems.filter((i) => next.includes(i.id)).map((i) => i.id),
    );
  };

  const setAmount = (id: string, value: string) => {
    const next = { ...amounts };
    if (value === '') delete next[id];
    else next[id] = Number(value);
    update('amounts', next);
  };

  const setDiscount = (id: string, value: string) => {
    const next = { ...discounts };
    if (value === '' || Number(value) === 0) delete next[id];
    else next[id] = Number(value);
    update('discounts', next);
  };

  const netOf = (id: string): number | null => {
    const gross = amounts[id];
    if (gross === undefined) return null;
    const percent = discounts[id] ?? 0;
    return percent ? Math.round(gross * (1 - percent / 100)) : gross;
  };

  const effectiveIds = includedIds.concat(
    tierItemId && !includedIds.includes(tierItemId) ? [tierItemId] : [],
  );
  const total = effectiveIds.reduce((sum, id) => sum + (netOf(id) ?? 0), 0);
  const grossTotal = effectiveIds.reduce((sum, id) => sum + (amounts[id] ?? 0), 0);
  const anyDiscount = effectiveIds.some((id) => (discounts[id] ?? 0) > 0);

  const oneTimeTotal = effectiveIds
    .filter((id) => kindOf(id) === 'ONE_TIME')
    .reduce((sum, id) => sum + (netOf(id) ?? 0), 0);
  const recurringTotal = effectiveIds
    .filter((id) => kindOf(id) === 'RECURRING')
    .reduce((sum, id) => sum + (netOf(id) ?? 0), 0);

  const selectedTerm = paymentTerms.find((t) => t.paymentTermId === config.paymentTermId);
  const isCustomTerm = Boolean(selectedTerm?.allowsCustomText);

  const onTermChange = (paymentTermId: string) => {
    const term = paymentTerms.find((t) => t.paymentTermId === paymentTermId);
    update('paymentTermId', paymentTermId);
    // A custom term keeps whatever text is already there; a fixed term takes
    // its approved wording.
    if (term && !term.allowsCustomText) update('paymentTerms', term.templateText);
    else if (term?.allowsCustomText) update('paymentTerms', '');
  };

  return (
    <div className="space-y-6">
      <Card
        title="Investment"
        description="Line items come from the approved template. Enter the amounts."
        actions={
          <div className="w-32">
            <label className="sr-only" htmlFor="currency">
              Currency
            </label>
            <Select
              id="currency"
              value={currency}
              disabled={disabled}
              onChange={(event) => update('currency', event.target.value as 'PHP' | 'USD')}
            >
              <option value="PHP">PHP</option>
              <option value="USD">USD</option>
            </Select>
          </div>
        }
      >
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left text-[11px] uppercase tracking-wide text-muted">
                <th scope="col" className="w-16 px-5 py-2.5 font-semibold">
                  Include
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold">
                  Item
                </th>
                <th scope="col" className="w-40 px-3 py-2.5 text-right font-semibold">
                  Amount
                </th>
                <th scope="col" className="w-28 px-3 py-2.5 text-right font-semibold">
                  Discount
                </th>
                <th scope="col" className="w-36 px-5 py-2.5 text-right font-semibold">
                  Net
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => {
                const included =
                  includedIds.includes(item.id) || item.id === tierItemId;
                const isFlagged = flagged.has(`amounts.${item.id}`);
                const net = netOf(item.id);
                return (
                  <tr
                    key={item.id}
                    className={[
                      'border-b border-rule last:border-0',
                      included ? '' : 'opacity-55',
                    ].join(' ')}
                  >
                    <td className="px-5 py-2.5">
                      <input
                        type="checkbox"
                        checked={included}
                        disabled={disabled}
                        aria-label={`Include ${item.label}`}
                        onChange={(event) => setIncluded(item.id, event.target.checked)}
                        className="h-4 w-4 accent-teal"
                      />
                    </td>
                    <td className="px-3 py-2.5 text-ink">
                      {item.label}
                      {item.optional && (
                        <span className="ml-2 rounded bg-tint px-1.5 py-0.5 text-[11px] text-muted">
                          optional
                        </span>
                      )}
                      {mixed && (
                        <span
                          className={[
                            'ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium',
                            kindOf(item.id) === 'RECURRING'
                              ? 'bg-sky-50 text-sky-800'
                              : 'bg-tint text-muted',
                          ].join(' ')}
                        >
                          {kindOf(item.id) === 'RECURRING' ? 'recurring' : 'one-time'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Input
                        type="number"
                        min={0}
                        step={1000}
                        inputMode="numeric"
                        aria-label={`Amount for ${item.label}`}
                        aria-invalid={isFlagged}
                        className={`text-right ${isFlagged ? 'border-red-400' : ''}`}
                        value={amounts[item.id] === undefined ? '' : String(amounts[item.id])}
                        disabled={disabled || !included}
                        onChange={(event) => setAmount(item.id, event.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          inputMode="numeric"
                          aria-label={`Discount percent for ${item.label}`}
                          className="text-right"
                          value={discounts[item.id] === undefined ? '' : String(discounts[item.id])}
                          disabled={disabled || !included}
                          onChange={(event) => setDiscount(item.id, event.target.value)}
                        />
                        <span className="text-muted">%</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-ink">
                      {net === null ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <>
                          {formatMoney(net, currency)}
                          {mixed && kindOf(item.id) === 'RECURRING' && (
                            <span className="text-muted"> {recurringSuffix}</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {mixed ? (
                <>
                  <tr className="bg-tint">
                    <td />
                    <td className="px-3 py-2.5 font-medium text-ink">One-time investment</td>
                    <td colSpan={2} />
                    <td className="px-5 py-2.5 text-right font-semibold tabular-nums text-ink">
                      {formatMoney(oneTimeTotal, currency)}
                    </td>
                  </tr>
                  <tr className="bg-tint">
                    <td />
                    <td className="px-3 py-2.5 font-medium text-ink">Monthly recurring</td>
                    <td colSpan={2} />
                    <td className="px-5 py-2.5 text-right font-semibold tabular-nums text-ink">
                      {recurringTotal > 0
                        ? `${formatMoney(recurringTotal, currency)} ${recurringSuffix}`
                        : '—'}
                    </td>
                  </tr>
                </>
              ) : (
                <tr className="bg-tint">
                  <td />
                  <td className="px-3 py-3 font-semibold text-ink">
                    {manifest.commercialTotalLabel}
                  </td>
                  <td className="px-3 py-3 text-right text-muted tabular-nums">
                    {anyDiscount ? formatMoney(grossTotal, currency) : ''}
                  </td>
                  <td className="px-3 py-3 text-right text-muted">
                    {anyDiscount ? `−${formatMoney(grossTotal - total, currency)}` : ''}
                  </td>
                  <td className="px-5 py-3 text-right text-[15px] font-semibold tabular-nums text-ink">
                    {formatMoney(total, currency)}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>

        {mixed && (
          <div className="mt-4 space-y-3">
            <p className="rounded-md bg-tint px-3 py-2 text-[12px] text-muted">
              One-time and monthly charges are kept apart. The proposal prints them
              separately —{' '}
              <span className="font-medium text-ink">
                {commercials?.totalText ?? '…'}
              </span>{' '}
              — rather than adding them into a single figure that would mean nothing.
            </p>

            {rules.offersContractValue && (
              <div className="rounded-md border border-rule px-3 py-2.5">
                <label className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={config.showYearOneContractValue === true}
                    onChange={(event) =>
                      update('showYearOneContractValue', event.target.checked)
                    }
                    className="mt-0.5 h-4 w-4 accent-teal"
                  />
                  <span>
                    <span className="block text-[13px] font-medium text-ink">
                      Calculate Year 1 indicative contract value
                    </span>
                    <span className="block text-[12px] text-muted">
                      {rules.contractValueNote}
                    </span>
                  </span>
                </label>

                {config.showYearOneContractValue && (
                  <p className="mt-2 border-t border-rule pt-2 text-[13px]">
                    <span className="text-muted">Year 1 indicative contract value:</span>{' '}
                    <span className="font-semibold tabular-nums text-ink">
                      {formatMoney(oneTimeTotal + recurringTotal * 12, currency)}
                    </span>
                    <span className="ml-2 text-[11px] text-muted">
                      ({formatMoney(oneTimeTotal, currency)} one-time +{' '}
                      {formatMoney(recurringTotal, currency)} × 12) · internal figure, not
                      printed
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {anyDiscount && (
          <p className="mt-4 rounded-md bg-tint px-3 py-2 text-[12px] text-muted">
            The approved Investment table has no discount row, so the document shows the net
            amount per line. The gross figure and the discount are kept on the proposal record
            for internal reporting.
          </p>
        )}
      </Card>

      <Card title="Payment terms" description="Chosen from the approved list.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Payment terms"
            htmlFor="paymentTermId"
            required
            error={flagged.has('paymentTerms') ? 'Required' : undefined}
          >
            <Select
              id="paymentTermId"
              value={config.paymentTermId ?? ''}
              disabled={disabled}
              onChange={(event) => onTermChange(event.target.value)}
            >
              <option value="" disabled>
                Select payment terms…
              </option>
              {paymentTerms.map((term) => (
                <option key={term.paymentTermId} value={term.paymentTermId}>
                  {term.displayName}
                </option>
              ))}
            </Select>
          </Field>

          {isCustomTerm && (
            <Field
              label="Custom payment terms"
              htmlFor="paymentTerms"
              required
              hint="Printed verbatim into the approved payment-terms sentence."
              error={flagged.has('paymentTerms') ? 'Required' : undefined}
            >
              <Input
                id="paymentTerms"
                value={config.paymentTerms ?? ''}
                maxLength={200}
                disabled={disabled}
                placeholder="e.g. 20% on signing, 40% at design sign-off, 40% on go-live"
                onChange={(event) => update('paymentTerms', event.target.value)}
              />
            </Field>
          )}
        </div>

        {config.paymentTerms && (
          <p className="mt-4 rounded-md bg-tint px-3 py-2 text-[12px] text-body">
            <span className="text-muted">Will print as:</span> “…Payment terms:{' '}
            {config.paymentTerms}.”
          </p>
        )}
      </Card>
    </div>
  );
}
